// What this studio last saw, held locally so the app can open without a network.
//
// The read half's storage (#207's U1): the overlay folds the write queue over
// what this returns, and the providers read through the overlay.
//
// ── One store keyed by the full path, plus a refresh receipt ───────────────
//
// One object store of documents keyed by `users/{uid}/{collection}/{id}` (#207
// Q1), **not** a store per collection, which would need an IndexedDB version
// bump every time `paths.js` grows. The full path also makes "forget this
// studio" a key-range delete, which is what a shared laptop needs at sign-out.
//
// The receipt is a **row**, not something inferred from `count > 0`.
//
// ── Three answers, and none of them is falsy ───────────────────────────────
//
// `useSettings` reads an absent document as **read**, and `normaliseStudio`
// defaults `locale` to `en-US`, from which `domain/locale.js` derives the
// currency. A store that cannot tell *we have never stored this* from *we
// stored that it is absent* would hand `null` to `normaliseStudio` on a cold
// offline open, and every amount in a British studio would change currency
// with nothing on screen that looks wrong (#207's failure 1). So three things
// are kept apart:
//
//   * **not yet known** — no receipt for this scope. Nothing has ever been
//     stored. The read reports `loading`.
//   * **not set** — a receipt exists and there is no row at that path. The
//     document is not in the window the listener delivered.
//   * **set to nothing** — a row with `exists: false`. The listener delivered
//     a snapshot that said this document does not exist. That is an answer, and
//     it is stored as one.
//
// `seenAt` is a millisecond stamp and **`0` is a legal value**, so nothing here
// tests it for truthiness; presence of the receipt row is the question, asked
// with `Object.prototype.hasOwnProperty` or an explicit `=== null`.
//
// **This file never defaults anything.** Normalisation belongs to the provider;
// `data` is the plain JSON the snapshot delivered.
//
// ── A snapshot does not deliver plain JSON, so it is made into some (#265) ─
//
// Every date-carrying field arrives as a Firestore `Timestamp`, a class
// instance. Both arms of `idbStore.js` copy a row through the structured clone
// algorithm (or a JSON fallback), which drops a custom prototype: a
// `Timestamp` would come back as `{ seconds, nanoseconds }` with `toDate()`
// gone, and a reader guarding `value?.toDate ? … : new Date(…)` would produce
// an **Invalid Date**.
//
// So every document is run through `docShape.toStoredDocument` on the way in
// and `docShape.fromStoredDocument` on the way out — the same carriage
// conversion the core boundary uses (see that file's header). It runs while the
// `Timestamp` still has its methods, so there is no prototype left for a clone
// to strip. What is stored is `{"$timestamp":"2026-09-16T…"}`, and what comes
// back is whatever `reviveTimestamp` says an instant is — the ISO string by
// default, a real `Timestamp` if the app hands in
// `(iso) => Timestamp.fromDate(new Date(iso))`.
//
// The same pass refuses what it cannot carry — `undefined`, `NaN`, a
// `DocumentReference` — by name, with the document path and the dotted
// sub-path. Those are not stored; `replaceCollection` and `replaceDocument`
// each say what they do about it, which differs.
//
// ── The three states ───────────────────────────────────────────────────────
//
//   * `loading` — nothing stored for this scope.
//   * `stale`   — stored rows, but no snapshot **in this session**: a reload,
//     or an offline cold start.
//   * `fresh`   — a snapshot landed this session.
//
// `stale` must not be spelled `loading: false`, or a read from before a
// withdrawal renders as current.
//
// **A stale read is marked, never blocked** (#207 Q5). This store reports the
// state and refuses nothing.
//
// **Not in the core.** Android shares the contract (#207 Q3) —
// `conflictClassFor`, `reconcileDocument`, the class tables in
// `core/src/reconcile.rs` — and not this model; freshness is what the core does
// not encode. The guard for these three states is `test/ui/appStore.test.jsx`.
// Nothing here decides what a stored value *means*: `exists`, `order`,
// `complete` and the three states are all statements about **carriage**.
//
// ── Why `order` is stored ──────────────────────────────────────────────────
//
// `orderBy('lastName')` (`useStudents.jsx`) is the only ordering a window asks
// for, and a store that returned rows in IndexedDB key order would return them
// in **id** order with nothing thrown or logged. So the receipt carries the
// ids as the snapshot delivered them.
//
// ── Wholesale, because that is what the listeners do ───────────────────────
//
// Every collection listener maps `snap.docs` whole on every callback, so a
// refresh is a replacement, not a merge: a document that leaves a `limit()`
// window leaves the store for the same reason it leaves the screen. The rows
// and the receipt go down in **one transaction**, which is what `complete` is
// for — a partial write interrupted by a closing tab must not read back as a
// full window.
//
// ── What is not here ───────────────────────────────────────────────────────
//
// No query engine: one `orderBy`, zero `where`, so "the listener decides the
// window, the store mirrors it". No eviction or TTL: the configured `limit()`s
// bound this at roughly 30,000 documents, which the providers already hold in
// memory. No cross-tab coordination: every tab writes the same snapshots of the
// same queries, last writer wins, and `persistentMultipleTabManager` handles
// the SDK's own cache separately.
//
// `test/ui/appStore.test.jsx` runs against the memory arm; `idbStore.js`'s
// IndexedDB arm runs under `fake-indexeddb` in `test/ui/idbStoreArms.test.jsx`
// (#251).

import { createLocalDatabase } from './idbStore';
import { UNCROSSABLE, fromStoredDocument, toStoredDocument } from './docShape';

const DB_NAME = 'coda-app-state';
const DB_VERSION = 1;

const DOCUMENTS = 'documents';
const RECEIPTS = 'receipts';

const STORES = [
  { name: DOCUMENTS, keyPath: 'path' },
  { name: RECEIPTS, keyPath: 'scope' },
];

const LOST = 'this studio is held in memory only, so a reload starts with nothing stored';

/** The three states a read can be in. Exported so nothing spells them twice. */
export const READ_STATE = {
  loading: 'loading',
  stale: 'stale',
  fresh: 'fresh',
};

// The largest UTF-16 code unit, so `prefix` .. `prefix + '￿'` covers every
// key that starts with `prefix`. Safe against astral characters in an id: those
// are surrogate pairs in the range D800–DFFF, all of which sort below FFFF.
const HIGH = '￿';

/**
 * The empty refusal list, shared and frozen (#379).
 *
 * `replaceCollection` and `replaceDocument` hand their result straight to
 * `useStoredCollection`/`useStoredDocument`, which put it in React state. A
 * fresh `[]` per snapshot is a new identity and therefore a re-render for a
 * fact that has not changed. Frozen so that a caller appending to it fails
 * loudly.
 */
const NO_REFUSALS = Object.freeze([]);

/** `users/{uid}/{collection}/{id}` — the key, and the only place it is built. */
export function documentPath(uid, collection, id) {
  return `users/${uid}/${collection}/${id}`;
}

/** The scope a collection listener refreshes. */
export function collectionScope(uid, collection) {
  return `users/${uid}/${collection}`;
}

/**
 * The scope a single-document listener refreshes.
 *
 * **Not the collection.** `settings/studio` and `settings/onboarding` are two
 * separate document listeners in one collection, so a receipt keyed by
 * `(uid, collection)` would let a snapshot of one mark the other as read. A
 * document scope is the document's own path; a collection scope is the
 * collection's. Both live in one receipt store.
 */
export function documentScope(uid, collection, id) {
  return documentPath(uid, collection, id);
}

/**
 * The database this store is built on. Exported so a test can hold one across
 * two store instances — see `createAppStore`'s `database` option.
 */
export function createAppStoreDatabase({ engine = 'auto' } = {}) {
  return createLocalDatabase({
    name: DB_NAME, version: DB_VERSION, stores: STORES, engine, lost: LOST,
  });
}

/**
 * A store.
 *
 * @param engine   `'auto'` in the app, `'memory'` in a test. See `idbStore.js`.
 * @param now      injectable clock, because `seenAt` is asserted on.
 * @param reviveTimestamp what a stored instant comes back as (#265). Injected
 *                 rather than fixed because building a real Firestore
 *                 `Timestamp` needs the SDK and `docShape.js` may not import
 *                 it; the app passes `(iso) => Timestamp.fromDate(new Date(iso))`.
 *
 *                 **It is exposed on the returned store, and `createOverlay`
 *                 defaults to it**: the overlay re-encodes an overlaid document
 *                 through `docShape` and revives it again, so two different
 *                 revivers would give one read two representations of an
 *                 instant.
 * @param database an existing database to build over, instead of opening one.
 *                 **This is what makes `stale` testable at all.** `fresh` means
 *                 "a snapshot landed in this session", and a session is a store
 *                 instance; with the memory engine a second `createAppStore()`
 *                 would otherwise get a second, empty database and read
 *                 `loading` where a reloaded tab reads `stale`. Two instances
 *                 over one database is a reload.
 */
export function createAppStore({
  engine = 'auto', now = () => Date.now(), database = null, reviveTimestamp = (iso) => iso,
} = {}) {
  const db = database || createAppStoreDatabase({ engine });

  /** A document `docShape` refused, as opposed to anything else that threw. */
  const isCarriageRefusal = (err) => err && err.code === UNCROSSABLE;

  const decode = (data) => fromStoredDocument(data, { reviveTimestamp });

  // Which scopes have had a **server** snapshot in this session. In memory:
  // `fresh` asks "has the server spoken to us since this page loaded". A new
  // store instance is a new session, which makes this testable without a page
  // load.
  //
  // **A snapshot from the SDK's own cache is not one (#682).** A cold start
  // whose SDK cache is populated delivers a `fromCache: true` window at once;
  // counted, every scope would go `fresh` before the server had said
  // anything, and the chip would say "Up to date" over data nobody had
  // confirmed. Only `fromCache: false` adds a scope here; a cached window is
  // stored, and read as `stale`, until the server's lands.
  const refreshedThisSession = new Set();

  // Which collection scopes the server has answered **in this session**
  // (#651), so that a cached snapshot arriving behind a server one cannot
  // write `confirmed: false` over it while the two are both in flight. The
  // durable answer is the receipt's `confirmed`; this only covers the gap
  // between a call starting and its receipt landing.
  const confirmedThisSession = new Set();

  // One write at a time per collection scope, in call order — see
  // `replaceCollection`. The tail swallows a failure so the next write still
  // runs; the caller of the failed one is the one told.
  const tails = new Map();
  const inScopeOrder = (scope, task) => {
    const run = (tails.get(scope) || Promise.resolve()).then(task);
    const tail = run.then(() => {}, () => {});
    tails.set(scope, tail);
    tail.then(() => { if (tails.get(scope) === tail) tails.delete(scope); });
    return run;
  };

  const receiptFor = (scope) => db.get(RECEIPTS, scope);

  /**
   * Which studios this device is holding stored reads for. See `studios`.
   *
   * Here rather than only on the returned object because `clearOtherStudios`
   * calls it, and a method calling a sibling through `this` breaks when the
   * store is destructured, as `overlay.js` and the test doubles do.
   */
  const studiosPresent = async () => {
    const receipts = await db.all(RECEIPTS);
    return [...new Set(receipts.map((r) => r.uid).filter(Boolean))];
  };

  /** One studio's rows and receipts, gone. See `clearStudio`. */
  const clearOneStudio = async (uid) => {
    const prefix = `users/${uid}/`;
    await db.apply([
      { store: DOCUMENTS, removeRange: [prefix, prefix + HIGH] },
      { store: RECEIPTS, removeRange: [prefix, prefix + HIGH] },
    ]);
    [...refreshedThisSession]
      .filter((scope) => scope.startsWith(prefix))
      .forEach((scope) => refreshedThisSession.delete(scope));
    [...confirmedThisSession]
      .filter((scope) => scope.startsWith(prefix))
      .forEach((scope) => confirmedThisSession.delete(scope));
  };

  return {
    /** See `createAppStore`'s parameter. `createOverlay` reads this. */
    reviveTimestamp,

    describe() {
      return db.describe();
    },

    /**
     * The whole window a collection listener just delivered.
     *
     * @param docs `[{ id, data }]` **in delivered order**. Order is taken from
     *             this array rather than from a comparator, because the
     *             ordering is Firestore's and we are mirroring it, not
     *             reproducing it.
     *
     * @param fromCache  the snapshot's `metadata.fromCache` (#651). The
     *             receipt records `confirmed` — the server has answered this
     *             scope on this device, in this snapshot or an earlier one —
     *             because only a durable mark can tell a device that never
     *             synced from one whose studio really has none of these:
     *             `core/src/reading.rs` has the argument. And only the
     *             server's window makes the scope `fresh` this session
     *             (#682). Absent, as from a caller that cannot say, it
     *             counts as the server's.
     *
     * Returns `{ refusals }` — the documents `docShape` would not carry, each
     * with its path and the dotted sub-path that stopped it.
     *
     * **A refused document is dropped from the window and kept in `order`**, so
     * the next `readCollection` reports `complete: false`, as for a row the
     * receipt names and the store has lost. Throwing would take the whole
     * roster down for one bad field and freeze the store at the previous
     * window. `overlay.js` makes the same call at the read end.
     */
    async replaceCollection(uid, collection, docs, { fromCache = false } = {}) {
      const scope = collectionScope(uid, collection);
      const prefix = `${scope}/`;
      const seenAt = now();
      // Before any await, so a later call sees it (see `confirmedThisSession`).
      if (!fromCache) confirmedThisSession.add(scope);
      const refusals = [];
      const rows = [];
      docs.forEach(({ id, data }) => {
        const path = documentPath(uid, collection, id);
        try {
          rows.push({
            store: DOCUMENTS,
            put: {
              path,
              uid,
              collection,
              id,
              data: toStoredDocument(path, data),
              exists: true,
              seenAt,
            },
          });
        } catch (err) {
          // For a document that cannot be carried, and not for a bug
          // underneath it: a `TypeError` from this file rethrows.
          if (!isCarriageRefusal(err)) throw err;
          refusals.push({ path, code: err.code, message: err.message });
        }
      });
      // In this scope's order (#651): a cached snapshot reads the receipt it
      // replaces, to carry `confirmed` forward, and without the queue a
      // server snapshot behind it could land first and then be overwritten by
      // the older window.
      await inScopeOrder(scope, async () => {
        const confirmed = confirmedThisSession.has(scope)
          || receiptConfirmed(await receiptFor(scope));
        await db.apply([
          // Every row of this collection goes, then the delivered window
          // lands. In one transaction, so a tab that dies between them leaves
          // the previous window intact rather than a half of the new one.
          { store: DOCUMENTS, removeRange: [prefix, prefix + HIGH] },
          ...rows,
          {
            store: RECEIPTS,
            put: {
              scope,
              uid,
              collection,
              kind: 'collection',
              seenAt,
              order: docs.map(({ id }) => id),
              complete: true,
              confirmed,
            },
          },
        ]);
      });
      // The server's window only (#682) — see `refreshedThisSession`.
      if (!fromCache) refreshedThisSession.add(scope);
      // One shared empty list when nothing was refused. `useStoredCollection`
      // puts this straight into `setStoreRefusals`, and a fresh `[]` there
      // would be a new identity, and a re-render, on every snapshot (#379).
      // The same object lets React's `Object.is` bail out.
      return { refusals: refusals.length ? refusals : NO_REFUSALS };
    },

    /**
     * One document a single-document listener just delivered.
     *
     * `data` is `null` when the snapshot says the document does not exist, and
     * that is **stored** — `exists: false` — rather than left as the absence of
     * a row. See the header.
     *
     * `fromCache` is the snapshot's `metadata.fromCache` (#682): a document
     * the SDK answered from its own cache is stored, and the scope stays
     * `stale` until the server's answer lands. Absent, it counts as the
     * server's, as in `replaceCollection`.
     *
     * Returns `{ refusals }`, and **a refused document is not stored at all —
     * not even as absent, and the receipt does not land either** — the
     * opposite of `replaceCollection`. Dropping a row from a window says "not
     * in the window"; dropping the only row of a document scope would say
     * `exists: false`, "the server says this document is not there", which on
     * `settings/studio` would default the studio's locale (see the header).
     * Writing nothing leaves the scope reading `loading` (never known) or
     * `stale` (the last document we could carry), both of which are true.
     */
    async replaceDocument(uid, collection, id, data, { fromCache = false } = {}) {
      const scope = documentScope(uid, collection, id);
      const seenAt = now();
      const exists = data !== null && data !== undefined;
      let stored = null;
      if (exists) {
        try {
          stored = toStoredDocument(documentPath(uid, collection, id), data);
        } catch (err) {
          if (!isCarriageRefusal(err)) throw err;
          return {
            refusals: [{
              path: documentPath(uid, collection, id), code: err.code, message: err.message,
            }],
          };
        }
      }
      await db.apply([
        {
          store: DOCUMENTS,
          put: {
            path: documentPath(uid, collection, id),
            uid,
            collection,
            id,
            data: stored,
            exists,
            seenAt,
          },
        },
        {
          store: RECEIPTS,
          put: {
            scope, uid, collection, kind: 'document', seenAt, order: null, complete: true,
          },
        },
      ]);
      // As `replaceCollection` (#682): a document the SDK answered from its
      // own cache is stored and stays `stale` until the server's lands.
      if (!fromCache) refreshedThisSession.add(scope);
      return { refusals: NO_REFUSALS };
    },

    /**
     * What is stored for a collection, in delivered order, with its state.
     *
     * Returns `{ docs, state, seenAt, complete, confirmed }`. `docs` is
     * `[{ id, data }]`. `confirmed` is whether the server has ever answered
     * this scope on this device (#651, see `replaceCollection`); `false`
     * before any receipt. The queue's unsent writes are **not** folded in
     * here — that is the overlay (U3), which wraps this.
     *
     * Never refuses and never throws on a stale read (#207 Q5).
     */
    async readCollection(uid, collection) {
      const scope = collectionScope(uid, collection);
      const receipt = await receiptFor(scope);
      if (receipt === null) {
        return {
          docs: [], state: READ_STATE.loading, seenAt: null, complete: false, confirmed: false,
        };
      }
      const prefix = `${scope}/`;
      const rows = await db.range(DOCUMENTS, prefix, prefix + HIGH);
      const byId = new Map(rows.map((row) => [row.id, row]));
      const order = receipt.order || [];
      const docs = [];
      let missing = 0;
      order.forEach((id) => {
        const row = byId.get(id);
        // A row the receipt names and the store does not have. Should not
        // happen — both go down in one transaction — so it is reported through
        // `complete` rather than silently shrinking the list, which is how a
        // half-populated page passes for a full one.
        if (!row) { missing += 1; return; }
        docs.push({ id: row.id, data: decode(row.data) });
      });
      return {
        docs,
        state: refreshedThisSession.has(scope) ? READ_STATE.fresh : READ_STATE.stale,
        seenAt: receipt.seenAt,
        complete: receipt.complete === true && missing === 0,
        confirmed: receiptConfirmed(receipt),
      };
    },

    /**
     * What is stored for one document, with its state.
     *
     * Returns `{ data, exists, state, seenAt }`. The three answers of the
     * header are `state === 'loading'` (never stored), `exists === false`
     * (stored, and the server says it is not there) and a `data` object.
     * `data` is `null` in the first two, so **`exists` is the field to read**,
     * not the truthiness of `data`.
     */
    async readDocument(uid, collection, id) {
      const scope = documentScope(uid, collection, id);
      const receipt = await receiptFor(scope);
      if (receipt === null) {
        return {
          data: null, exists: false, state: READ_STATE.loading, seenAt: null,
        };
      }
      const row = await db.get(DOCUMENTS, documentPath(uid, collection, id));
      return {
        data: row && row.exists ? decode(row.data) : null,
        exists: Boolean(row && row.exists),
        state: refreshedThisSession.has(scope) ? READ_STATE.fresh : READ_STATE.stale,
        seenAt: receipt.seenAt,
      };
    },

    /**
     * Which studios this device is holding stored reads for (#302).
     *
     * **Read off the receipts rather than the documents**: a receipt is one
     * row per collection or watched document, a document one row per document
     * (up to about 30,000), so this is a handful of rows whatever the roster.
     *
     * Sound because `replaceCollection` and `replaceDocument` each write their
     * rows **and** their receipt in one `db.apply`, so a stored document cannot
     * exist without a receipt under the same `users/{uid}/` prefix. A refused
     * document leaves the receipt and drops the row, never the other way round.
     * If that ever stopped holding, documents under a uid with no receipt would
     * survive the sweep below; `reset()` clears those.
     */
    studios: studiosPresent,

    /**
     * Forget every studio on this device except this one (#302).
     *
     * **What `clearStudio` at a sign-out cannot reach.** `AuthContext`'s auth
     * observer ends a session for every way a session ends that *runs code* — a
     * sign-out, a revoked token, a password changed elsewhere, a sign-out in
     * another tab. A closed tab runs none of them, so `coda-app-state` would
     * keep every document and receipt under that uid on a device somebody else
     * then signs in on. Every read is keyed by the signed-in uid, so it never
     * reaches the screen; it is data at rest.
     *
     * The write queue and the outstanding-write ledger are *scoped* rather than
     * deleted (#282), because they are work still owed to the teacher who made
     * it. This store is a cache of what the server said, re-fetchable by whoever
     * it belongs to, so a foreign studio's rows are deleted.
     *
     * Called when a session claims the store.
     *
     * Returns the uids it cleared, so a caller can say what it did.
     */
    async clearOtherStudios(uid) {
      const present = await studiosPresent();
      const foreign = present.filter((studio) => studio !== uid);
      // Sequentially: each is a two-store transaction, and a handful of studios
      // on one device is the whole range this ever runs over.
      for (const studio of foreign) {
        await clearOneStudio(studio);
      }
      return foreign;
    },

    /**
     * Forget everything stored for one studio.
     *
     * A key-range delete over `users/{uid}/`, which is the reason the key is the
     * full path. `clearOtherStudios` above is the half of #302 a sign-out
     * cannot reach. When to call it is the caller's.
     */
    clearStudio: clearOneStudio,

    /** Test seam, and what a sign-out of everything would want. */
    async reset() {
      await db.apply([
        { store: DOCUMENTS, clear: true },
        { store: RECEIPTS, clear: true },
      ]);
      refreshedThisSession.clear();
      confirmedThisSession.clear();
    },
  };
}

/**
 * Whether a receipt says the server has answered its scope (#651).
 *
 * **A receipt without the `confirmed` mark counts as confirmed.** Counted as
 * unconfirmed, every synced device's empty lists would say "not loaded on this
 * device" offline until the server next answered them. No receipt is no
 * answer at all.
 */
function receiptConfirmed(receipt) {
  return receipt !== null && receipt !== undefined && receipt.confirmed !== false;
}
